import { useVideoJs } from '@/hooks/useVideoJs';
import { RoomContext, VideoChatContext } from '@/store';
import { useUserMonitor } from '@/user-monitor';
import { Box, useMediaQuery } from '@mui/material';
import { useTheme, styled } from '@mui/material/styles';
import React, { FC, useContext, useEffect, useRef, useState } from 'react';

import { isMobileDevice } from '@/lib/utils/detect';

interface IProps {
  source: {
    poster?: string;
    src: string;
    type: string;
  };
  trackingdata?: {
    programroomId?: string;
    videoTitle?: string;
  };
  children?: React.ReactNode;
  videoStartTime?: any;
  videoDurationTime?: any;
  event?: any;
  videoAssetStartUTC?: any;
  playerEventCallback?: any;
  storyBoardUrl?: any;
  captions?: any;
}
interface Statsdata {
  programroomId?: string;
  videoTitle?: string;
  videoSeries?: string;
}

// Styled component for the video player
const StyledVideo = styled('video', {
  shouldForwardProp: (prop) => prop !== 'isLive',
})<{ isLive?: boolean }>(({ isLive }) => ({
  width: '100%',
  height: '100%',
  textAlign: 'center',
  backgroundColor: '#151515',
  '& > .vjs-tech': {
    position: 'relative',
    maxWidth: '100%',
  },
  '& > .vjs-poster': {
    backgroundColor: 'inherit',
    backgroundSize: isLive ? 'cover' : 'contain',
  },
  '&.video-js .vjs-progress-control': {
    visibility: isLive ? 'hidden' : 'visible',
    cursor: isLive ? 'default' : 'pointer',
  },
}));

const useStyles = () => ({
  actions: {
    top: '0 !important',
    right: '0 !important',
    position: 'absolute !important',
    display: 'flex !important',
    flexDirection: 'column !important',
    alignItems: 'flex-end !important',
    padding: '8px !important', // Fixed value instead of theme.spacing
    '& > *': {
      marginBottom: '8px !important', // Fixed value instead of theme.spacing
    },
  },
});

/**
 * Renders a videoJs player
 *
 * @returns JSX.Element
 * @example
 * <VideoPlayer
 *   source={{
 *     src: 'path/to/video',
 *     type: 'application/x-mpegURL',
 *     poster: 'path/to/posterImg'
 *   }}
 *  trackingdata={{
 *    programroomId: 'rooomId'
 *    videoTitle: 'room title',
 *  }}
 * >
 *   // pass an optional component to render in video header area
 * </VideoPlayer>
 */

export type VideoPlayerHandle = {
  changedStatsMetaData: (data: Statsdata) => void;
};

export const VideoPlayer = React.forwardRef<
  VideoPlayerHandle,
  React.PropsWithChildren<IProps>
>((props: IProps, ref) => {
  const classes = useStyles();

  const { videoRef, playerRef } = useVideoJs({
    liveTracker: {
      trackingThreshold: 2,
    },
    controlBar: {
      timeDivider: false,
      currentTimeDisplay: false,
      durationDisplay: false,
    },
    poster: props.source.poster,
    sources: [
      {
        type: props.source.type,
        src: props.source.src,
      },
    ],
    plugins: {
      mux: {
        debug: false,
        data: {
          env_key: process.env.MUX_ENV_KEY, //required
          // Metadata
          player_name: 'COEO Player',
          player_init_time: Date.now(),
          sub_property_id: props.trackingdata
            ? props.trackingdata.programroomId
            : null,
          video_title: props.trackingdata
            ? props.trackingdata.videoTitle
            : null,
          //video_stream_type: 'live',
        },
      },
    },
  });

  //Emit mux programchange on new currentevent/session
  React.useImperativeHandle(ref, () => ({
    changedStatsMetaData: (data: Statsdata) => {
      if (playerRef.current) {
        if (!playerRef.current.paused()) {
          //mux is a plugin to videojs
          (playerRef.current as any).mux.emit('programchange', {
            sub_property_id: data.programroomId || null,
            video_title: data.videoTitle || null,
            video_series: data.videoSeries || null,
          });
        }
      }
    },
  }));

  return (
    <div data-vjs-player>
      {props.children && <Box sx={classes.actions}>{props.children}</Box>}
      <StyledVideo
        ref={videoRef}
        isLive={false}
        className="video-js vjs-big-play-centered"
        playsInline
      />
    </div>
  );
});

interface ILivePlayerProps extends Pick<IProps, 'source'> {
  children?: React.ReactNode;
}

export const VideoPlayerLive: FC<ILivePlayerProps> = ({ source, children }) => {
  const theme = useTheme();
  const classes = useStyles();

  const { videoChatId } = useContext(VideoChatContext);
  const {
    roomState: { room },
  } = useContext(RoomContext);

  const { hasInteracted } = useUserMonitor();
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'), {
    noSsr: true,
  });

  const { videoRef, playerRef } = useVideoJs({
    liveTracker: {
      trackingThreshold: 2,
    },
    liveui: true,
    autoplay: !smallScreen && hasInteracted,
    controlBar: {
      timeDivider: false,
      currentTimeDisplay: false,
    },
    poster: source.poster,
    sources: [
      {
        type: source.type,
        src: source.src,
      },
    ],
    plugins: {
      mux: {
        debug: false,
        data: {
          env_key: process.env.MUX_ENV_KEY,
          player_name: 'COEO Player',
          player_init_time: Date.now(),
          sub_property_id: room.id || null,
          video_title: room.title || null,
        },
      },
    },
  });

  useEffect(() => {
    try {
      if (videoChatId === null) {
        playerRef.current && playerRef.current.volume(1);
      } else {
        playerRef.current && playerRef.current.volume(0.15);
      }
    } catch (e) {
      console.log('Could not set volume');
    }
  }, [videoChatId]);

  useEffect(() => {
    if (playerRef.current != null) {
      if (!playerRef.current.paused()) {
        (playerRef.current as any).mux.emit('programchange', {
          sub_property_id: room.id || null,
          video_title: room.title || null,
          video_series: room.currentEvent?.title || null,
        });
      }
    }
  }, [room.currentEventId]);

  return (
    <div data-vjs-player>
      {children && <Box sx={classes.actions}>{children}</Box>}
      <StyledVideo
        ref={videoRef}
        isLive={true}
        className="video-js vjs-big-play-centered"
        playsInline
      />
    </div>
  );
};

export const VideoPlayerVod = React.forwardRef<
  VideoPlayerHandle,
  React.PropsWithChildren<IProps>
>((props: IProps, ref) => {
  // Using ref in useImperativeHandle below
  const classes = useStyles();
  const videoassetstartutctime = useRef(0);
  const { hasInteracted } = useUserMonitor();
  const [captions, setCaptions] = useState([]);

  const { videoRef, playerRef } = useVideoJs({
    autoplay: !isMobileDevice() && hasInteracted,
    preload: 'metadata',
    playbackRates: [1, 1.2, 1.3, 1.4, 1.5],
    poster: props.source.poster,
    sources: [
      {
        type: props.source.type,
        src: props.source.src,
      },
    ],

    plugins: {
      mux: {
        debug: false,
        data: {
          env_key: process.env.MUX_ENV_KEY, //required
          // Metadata
          player_name: 'COEO Player',
          player_init_time: Date.now(),
          video_stream_type: 'vod',
        },
      },

      // Example solution eliminating dependency of useEffect and forwardRef.
      //   offset: {
      //     start: props.videoStartTime / 1000,
      //     end: (props.videoStartTime + props.videoDurationTime) / 1000,
      //     restart_beginning: false, //Should the video go to the beginning when it ends
      //   }
    },
  });

  const handleKeyDown = (event: any) => {
    let player = playerRef.current;
    if (event.key == 'ArrowLeft') {
      //left arrow
      player.currentTime(player.currentTime() - 1);
    } else if (event.key == 'ArrowRight') {
      //right arrow
      player.currentTime(player.currentTime() + 1);
    }
  };

  useEffect(() => {
    videoassetstartutctime.current = props.videoAssetStartUTC;
    let player = playerRef.current;

    if (player != null) {
      //Add arrow to navigate forward
      window.addEventListener('keydown', handleKeyDown);

      //tap pause
      /*
      if(isMobileDevice()){
        player.on('touchstart', function (event:any) {
          let player = playerRef.current;
          if (event.target.nodeName === 'VIDEO') {
            if (player.paused()) {
              player.play()
            } else {
              player.pause()
            }
          }
        });
      }
      */

      if (props.storyBoardUrl) {
        if (!isMobileDevice()) {
          // player.vttThumbnails({
          //   src: props.storyBoardUrl,
          //   showTimestamp: true,
          // });
        }
      }

      const start_offset = 0;
      const p = playerRef.current as any;
      // video-offset registers player.offset(); duplicate video.js bundles or load order
      // can leave offset missing — guard to avoid crashing preview / dashboard playback.
      if (typeof p.offset === 'function') {
        p.offset({
          start: start_offset,
        });
      }

      player.on('ended', () => {
        let playerutctime =
          Number(videoassetstartutctime.current) +
          Number(player.currentTime() - start_offset);
        if (props.playerEventCallback instanceof Function) {
          props.playerEventCallback('ended', playerutctime);
        }
      });
      player.on('error', () => {
        // Notify parent so it can fallback to public URL on 403 (e.g. public playback ID)
        if (props.playerEventCallback instanceof Function) {
          const src = props.source?.src ?? '';
          const playbackId =
            src.match(/stream\.mux\.com\/([^/.]+)/)?.[1] ?? null;
          props.playerEventCallback('error', 0, { playbackId });
        }
      });
      player.on('seeked', () => {
        if (!player.seeking() && !player.scrubbing()) {
          let playerutctime =
            Number(videoassetstartutctime.current) +
            Number(player.currentTime() - start_offset);
          if (props.playerEventCallback instanceof Function) {
            props.playerEventCallback('seeked', playerutctime);
          }
        }
      });

      if (props.videoStartTime && props.videoAssetStartUTC) {
        player.on('timeupdate', function () {
          if (!player.scrubbing()) {
            let playerutctime =
              Number(videoassetstartutctime.current) +
              Number(player.currentTime() - start_offset);
            if (player.currentTime() > 0) {
              if (props.playerEventCallback instanceof Function) {
                props.playerEventCallback('timeupdate', playerutctime);
              }
            }
          }
        });

        //Offset is a loaded videojs plugin
        /*(playerRef.current as any).offset({
        start: props.videoStartTime / 1000,
        end: (props.videoStartTime + props.videoDurationTime) / 1000,
        restart_beginning: false, //Should the video go to the beginning when it ends
      });
      */
      }
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    videoassetstartutctime.current = props.videoAssetStartUTC;
    let player = playerRef.current;
    if (player != null) {
      if (props.videoStartTime < 0) {
        player.currentTime(0);
      } else {
        //Abs check Needed to fix clicks in sound when swap
        if (Math.abs(player.currentTime() - props.videoStartTime) > 1) {
          player.currentTime(props.videoStartTime);
        }
      }
      if (player.paused()) {
        //player.play();
      }
    }
  }, [props.videoStartTime, props.videoAssetStartUTC]);

  useEffect(() => {
    let player = playerRef.current;
    if (player != null) {
      player.src([
        {
          type: props.source.type,
          src: props.source.src,
        },
      ]);

      //player.load();
      player.currentTime(props.videoStartTime);
      var playPromise = player.play();

      if (playPromise !== undefined) {
        playPromise
          .then((_) => {})
          .catch((_error) => {
            // Auto-play was prevented
            //console.log("jan error", _error);
          });
      }

      //console.log("current src", player.currentSrc());
      //player.play();
    }
  }, [props.source.src]);

  useEffect(() => {
    return () => {
      if (playerRef.current) {
        playerRef.current.dispose();
        playerRef.current = null;
      }
    };
  }, [playerRef]);

  // Exposes a method to the parent via ref so it can trigger a Mux "programchange" event
  // with updated video metadata while the player is playing.
  React.useImperativeHandle(ref, () => ({
    changedStatsMetaData: (data: Statsdata) => {
      if (playerRef.current && !playerRef.current.paused()) {
        (playerRef.current as any).mux.emit('programchange', {
          sub_property_id: data.programroomId || null,
          video_title: data.videoTitle || null,
          video_series: data.videoSeries || null,
        });
      }
    },
  }));

  useEffect(() => {
    const player = playerRef.current;
    if (player != null) {
      player.ready(function () {
        if (JSON.stringify(props.captions) !== JSON.stringify(captions)) {
          props.captions.map((caption: any) => {
            player.addRemoteTextTrack(
              {
                label: caption.subtitle_name,
                srclang: caption.subtitle_language,
                src: `/api/programvideoassets/${caption.programroomvideoassetId}/subtitles/link/${caption.id}`,
              },
              false
            );
          });
          setCaptions(props.captions);
        }
      });
    }
  }, [props.captions]);

  return (
    <div data-vjs-player>
      {props.children && <Box sx={classes.actions}>{props.children}</Box>}
      <StyledVideo
        ref={videoRef}
        isLive={false}
        className="video-js vjs-big-play-centered"
        playsInline
      />
    </div>
  );
});
